import { cn } from "@/lib/utils";
import env from "@/env";
import { AuthMachineContext } from "@/store/auth";
import { MenuItem } from "@/types";
import { trackEvent } from "@/utils/analytics";
import { slugify } from "@/utils/string";
import { useMemo, useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Link, useLocation } from "wouter";

type MainMenuProps = {
  items?: MenuItem[];
  className?: string;
  isActive?: boolean;
  defaultActive?: string;
  toolName?: string;
  onClick?: (value: string) => void;
};

export const HorizontalMenu: React.FC<MainMenuProps> = ({
  items = [],
  className = "",
  defaultActive,
  onClick
}) => {
  const getDefaultActive = () => defaultActive || (items.length ? items[0].value : "");
  const [location] = useLocation();
  const [activeItem, setActiveItem] = useState(getDefaultActive);
  const isLoggedIn = AuthMachineContext.useSelector((state) => state.context.isLoggedIn);

  const handleClick = (item: MenuItem) => {
    setActiveItem(item.value);
    onClick?.(item.value);

    if (item.analyticEvent) {
      trackEvent(item.analyticEvent, { tab: item.label });
    }
  };

  const allowedItems = useMemo(() => {
    const flattenedItems = items.flatMap((item) =>
      item.subMenuItems?.length ? item.subMenuItems : [item]
    );
    let filteredItems = flattenedItems;
    if (env.FEATUREFLAG_DELAYED_LOGIN && !isLoggedIn) {
      filteredItems = flattenedItems
        .filter(({ requiresLogin, showInDelayedLogin }) => {
          if (showInDelayedLogin) {
            return true;
          }
          return requiresLogin !== true;
        })
        .sort((a, b) => {
          if (isLoggedIn) {
            return 0;
          }
          if (a.showInDelayedLogin && !b.showInDelayedLogin) {
            return 1;
          }
          if (!a.showInDelayedLogin && b.showInDelayedLogin) {
            return -1;
          }
          return 0;
        });
    }
    return filteredItems;
  }, [items, isLoggedIn]);

  return (
    <div
      data-testid="MainMenu"
      className={`flex flex-row justify-start overflow-x-auto border-b-[1px] pl-[10px] dark:border-b-gray-dark-500 ${className}`}
    >
      {allowedItems.map((item) => {
        const { value, label, href, disabled, showInDelayedLogin } = item;
        const active =
          typeof item.active !== "undefined"
            ? item.active
            : href
              ? location.slice(0, href?.length) === href
              : value === activeItem;

        const linkClassName = `group relative mx-1.5 mb-[-1px] box-border flex flex-row items-center gap-2 rounded-t-md border-b-[2px] border-white px-1.5
              py-2 hover:text-foreground hover:before:absolute hover:before:bottom-[1px] hover:before:left-[30%] hover:before:right-[30%] hover:before:top-[50%]
              hover:before:block hover:before:bg-gray-dark-800 hover:before:opacity-0 dark:hover:before:opacity-80 hover:before:blur-[12px] mb-0 dark:border-transparent
              ${
                disabled
                  ? `pointer-events-none opacity-20`
                  : active
                    ? `border-foreground from-gray-light-600 to-gray-light text-black before:absolute before:bottom-[1px] before:left-[30%] before:right-[30%] before:top-[50%]
                       before:block before:bg-gray-dark-800 before:opacity-0 dark:before:opacity-85 before:blur-[12px] dark:border-b-white dark:${href ? "bg-gray-dark-600" : "bg-black"} dark:text-foreground`
                    : `cursor-pointer bg-white text-secondary-foreground hover:text-black border-b-[3px] dark:${href ? "bg-gray-dark-600" : "bg-black"} dark:hover:text-foreground`
              }`;

        const tooltipProps =
          showInDelayedLogin && !isLoggedIn
            ? {
                "data-tooltip-id": `MainMenu_${slugify(value)}`,
                "data-tooltip-content": "This feature is available after you log in."
              }
            : {};
        return href ? (
          <>
            <Link
              href={href}
              key={value}
              data-testid={`MainMenu_${slugify(value)}`}
              className={cn(
                linkClassName,
                item.className,
                showInDelayedLogin && !isLoggedIn
                  ? "before:hidden before:opacity-0 hover:before:opacity-0"
                  : ""
              )}
              onClick={(e) => {
                if (showInDelayedLogin && !isLoggedIn) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                handleClick(item);
              }}
              {...tooltipProps}
            >
              <span
                className={cn(
                  "relative z-[1] whitespace-nowrap text-sm",
                  showInDelayedLogin && !isLoggedIn ? "text-foreground opacity-20" : ""
                )}
              >
                {label}
              </span>
              {disabled && <span className="whitespace-nowrap text-sm">[soon]</span>}
            </Link>
            <ReactTooltip
              style={{
                fontSize: "14px",
                color: "#999999",
                borderColor: "#1e1e1e",
                borderRadius: "8px"
              }}
              border={1}
              place="bottom"
              id={`MainMenu_${slugify(value)}`}
            />
          </>
        ) : (
          <div
            key={value}
            onClick={() => handleClick(item)}
            className={cn(linkClassName, item.className)}
            data-testid={`MainMenu_${slugify(value)}`}
          >
            <span className="relative z-[1] whitespace-nowrap text-sm">{label}</span>
          </div>
        );
      })}
    </div>
  );
};
